import React, { useEffect, useState } from 'react';
import { Button, Table, Spin } from 'antd';
import type { TableProps } from 'antd';
import { WarningOutlined, DownOutlined, UpOutlined, DownloadOutlined, CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { mixGetV3 } from '../../../services/mixin';
import { API_URL_v3 } from '../../../env';
import { useSelector } from 'react-redux';
import { selectorInfoUser } from '../../../store/slices/infoUserSlice';

const PAGE_SIZE_OPTIONS = ['10', '15', '20', '50'];

const getErrors = (record: any): string => {
  const raw = record.errors ?? record.error_details ?? record.errorDetails;
  if (!raw) return 'Lỗi không xác định';
  if (Array.isArray(raw)) return raw.length > 0 ? raw.join(', ') : 'Lỗi không xác định';
  return raw;
};

const ImportDataStep4 = ({ stData = {} as any, onChange = (data = {}, type = null) => {} }) => {
  const user = useSelector(selectorInfoUser);
  const [expandedFileName, setExpandedFileName] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const [errorRows, setErrorRows] = useState<any[]>([]);
  const [totalErrorRows, setTotalErrorRows] = useState<number>(0);
  const [loadingRows, setLoadingRows] = useState<boolean>(false);
  const [dynamicColumns, setDynamicColumns] = useState<TableProps<any>['columns']>([]);

  const taskId: string = stData.reviewTaskId || '';
  const reviewFiles: any[] = stData.reviewFiles || [];
  const totalInvalid: number = stData.reviewTotalInvalid || 0;
  const isSkippable: boolean = stData.reviewIsSkippable !== false;
  const requiredWarnings: Record<string, string[]> = stData.reviewRequiredWarnings || {};

  // Map dataFiles by name for icon/source info
  const dataFileMap: Record<string, any> = {};
  (stData.dataFiles || []).forEach((f: any) => { dataFileMap[f.name] = f; });

  const HIDDEN_FIELDS = ['_file', '_phase', '_match_reason'];

  const getColumnTitle = (key: string, fileName: string): string => {
    const fileParams = stData.filesParams?.[fileName];
    if (fileParams) {
      const crmColumnId = fileParams[key];
      if (crmColumnId && stData.obSourceColumns?.[crmColumnId]?.name) {
        return stData.obSourceColumns[crmColumnId].name;
      }
    }
    if (stData.obSourceColumns?.[key]?.name) {
      return stData.obSourceColumns[key].name;
    }
    return key;
  };

  const buildColumns = (rows: any[], fileName: string): TableProps<any>['columns'] => {
    if (!rows.length || !rows[0].data) return [];
    const apiKeys = Object.keys(rows[0].data).filter(k => !HIDDEN_FIELDS.includes(k));

    // Reorder keys based on headers from dataFiles (same order as Step 2/3)
    const matchedFile = dataFileMap[fileName];
    const headers: string[] = matchedFile?.headers || [];
    const fileParams = stData.filesParams?.[fileName] || {};
    let orderedKeys: string[];
    if (headers.length) {
      // Use header order, only include mapped columns (filesParams[val] !== null)
      const mappedKeys = headers.filter(h => fileParams[h] !== null && apiKeys.includes(h));
      // Append any API keys not in headers at the end
      const remaining = apiKeys.filter(k => !mappedKeys.includes(k));
      orderedKeys = [...mappedKeys, ...remaining];
    } else {
      orderedKeys = apiKeys;
    }

    const cols: TableProps<any>['columns'] = [
      {
        title: 'STT',
        width: 60,
        render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
      },
      ...orderedKeys.map(key => ({
        title: getColumnTitle(key, fileName),
        dataIndex: ['data', key],
        ellipsis: true,
        render: (val: any) => val ?? '—',
      })),
      {
        title: 'Lỗi',
        dataIndex: 'errors',
        fixed: 'right' as const,
        width: 200,
        render: (_: any, record: any) => (
          <span style={{ color: '#ff4d4f' }}>{getErrors(record)}</span>
        ),
      },
    ];
    return cols;
  };

  const handleDownloadErrorFile = async (filename: string) => {
    if (!taskId) return;
    try {
      const res = await fetch(`${API_URL_v3}/result/file_export_review/${taskId}?filename=${encodeURIComponent(filename)}&admin_id=${user.id}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download error:', e);
    }
  };

  const fetchErrorRows = async (filename: string, offset: number, limit: number) => {
    if (!taskId) return;
    setLoadingRows(true);
    try {
      const res = await mixGetV3(`/result/file_import_review/${taskId}?filename=${encodeURIComponent(filename)}&offset=${offset}&limit=${limit}`, { admin_id: user.id }, { skipVehicleBrand: true });
      if (res && res.data) {
        const rows = res.data.map((item: any, i: number) => ({ ...item, key: item.row_index ?? i }));
        setErrorRows(rows);
        setTotalErrorRows(res.error_count ?? res.total_rows ?? rows.length);
        setDynamicColumns(buildColumns(rows, filename));
      }
    } finally {
      setLoadingRows(false);
    }
  };

  return (
    <>
      <h3 className='cts-h3-part-4'>Kiểm tra dữ liệu cần xử lý</h3>

      {totalInvalid === 0 && reviewFiles.length === 0 && (
        <div className="step4-warning-banner" style={{ borderColor: '#52c41a', backgroundColor: '#f6ffed' }}>
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18, marginRight: 8, flexShrink: 0 }} />
          <span>
            Tất cả dữ liệu đều hợp lệ, không có bản ghi lỗi nào. Vui lòng nhấn <strong>"Tiếp theo"</strong> để tiếp tục.
          </span>
        </div>
      )}

      {totalInvalid > 0 && (
        <div className="step4-warning-banner">
          <WarningOutlined className="step4-warning-icon" />
          <span>
            Hệ thống phát hiện <strong>{totalInvalid} bản ghi dữ liệu lỗi</strong> và sẽ không được import vào hệ thống.
            Vui lòng tải file lỗi để kiểm tra và import lại, hoặc nhấn "Tiếp theo" để bỏ qua các bản ghi lỗi này.
          </span>
        </div>
      )}

      {!isSkippable && Object.keys(requiredWarnings).length > 0 && (
        <div className="step4-warning-banner" style={{ borderColor: '#ff4d4f', backgroundColor: '#fff2f0' }}>
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18, marginRight: 8, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4, color: '#ff4d4f' }}>
              Dữ liệu chứa lỗi bắt buộc phải xử lý trước khi tiếp tục:
            </div>
            {Object.entries(requiredWarnings).map(([fileName, warnings]) => (
              <div key={fileName} style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 500 }}>{fileName}:</span>{' '}
                <span style={{ color: '#ff4d4f' }}>{(warnings || []).join(', ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="step4-file-list">
        {reviewFiles.map((file: any, index: number) => {
          const fileName = file.filename || file.file_name || file.name;
          const isExpanded = expandedFileName === fileName;
          const matchedDataFile = dataFileMap[fileName];
          const fileIcon = matchedDataFile?.icon || '/images/icons/xlssIcon.png';
          const sourceTag = matchedDataFile?.isSource || '';
          const invalidCount = file.invalid_count ?? file.error_count ?? 0;

          return (
            <div key={fileName || index} className="step4-file-card">
              <div className="step4-file-card-header">
                <div className="step4-file-info">
                  <img src={fileIcon} loading="lazy" alt="file" className="xls-icon" />
                  <span className="step4-file-name" title={fileName}>{fileName}</span>
                  {sourceTag && <span className="file-tag">{sourceTag}</span>}
                </div>
                <div className="step4-file-actions">
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    className="step4-download-link"
                    onClick={() => handleDownloadErrorFile(fileName)}
                  >
                    Tải danh sách
                  </Button>
                  <Button
                    type="link"
                    icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => {
                      if (isExpanded) {
                        setExpandedFileName(null);
                      } else {
                        setExpandedFileName(fileName);
                        setCurrentPage(1);
                        fetchErrorRows(fileName, 0, pageSize);
                      }
                    }}
                    className="step4-toggle-btn"
                  >
                    {isExpanded ? 'Rút gọn' : 'Xem chi tiết'}
                  </Button>
                </div>
              </div>

              {isExpanded && (
                <div className="step4-file-card-body">
                  {loadingRows ? (
                    <div className="step4-loading"><Spin /></div>
                  ) : (
                    <Table
                      className="cts-table none-go-to-page step4-error-table"
                      columns={dynamicColumns}
                      dataSource={errorRows}
                      scroll={{ x: 'max-content' }}
                      tableLayout="auto"
                      pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalErrorRows,
                        showSizeChanger: true,
                        pageSizeOptions: PAGE_SIZE_OPTIONS,
                        showTotal: (total) =>
                          `Tổng cộng ${Math.ceil(total / pageSize)} trang`,
                        onChange: (page, size) => {
                          setCurrentPage(page);
                          setPageSize(size);
                          fetchErrorRows(fileName, (page - 1) * size, size);
                        },
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ImportDataStep4;
